import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Popover, Whisper } from "rsuite";
import { Button } from "reactstrap";

import useModule from "../../hooks/useModule";
import WidgetError from "./WidgetError";
import useToggle from "../../hooks/useToggle";
import SortBy from "../PageView/PageViewTable/SortBy";
import TableActionsMenu from "../CRM/TableActionsMenu";
import UpdateRecordsDrawer from "../Drawers/Records/UpdateRecordsDrawer";
import usePagePermissions from "../../hooks/usePagePermissions";
import StrUtils from "../../utils/StrUtils";

import { Field } from "../../types/TenantInterfaces";
import PopoverDropdownMenu from "../Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../Menus/DropdownMenuDivider";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import QuickCreateMenu from "../Menus/QuickCreateMenu";
import FiltersButton from "../SavedFilters/FiltersButton";
import DotsOptionsMenu from "../Menus/DotsOptionsMenu";
import AppTable from "../AppTable/AppTable";
import CreateRecordDrawer from "../Drawers/Records/CreateRecordDrawer";
import { Prefill } from "../../types/GlobalInterfaces";

const emptyPrefill: [] = [];

const TableSettingsOptions = (
  openReorderSettings: any,
  openSettings: any,
  disabled: boolean = false
) => {
  return (
    <PopoverDropdownMenu>
      <h5 className="px-2 pt-2 text-dark">Manage</h5>
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Reorder Table Columns"
        onClick={() => {
          openReorderSettings();
        }}
        icon="mdi mdi-reorder-vertical"
        disabled={disabled}
      />
      <DropdownOptionWithIcon
        name="Settings"
        onClick={() => {
          openSettings();
        }}
        icon="mdi mdi-table-cog"
      />
    </PopoverDropdownMenu>
  );
};

const TableWidgetV2 = ({
  section,
  setSettings,
  useToolbar,
  updateWidget,
  record,
}: {
  section: any;
  setSettings: (section: any) => void;
  useToolbar?: boolean;
  updateWidget: (updatedWidget: any) => void;
  record?: any;
}) => {
  const { pageID } = useParams();
  const { module } = useModule(section?.ModuleAPIName);
  const [query, setQuery] = useState<any>(null);
  const [selectedPrefill, setSelectedPrefill] = useState<Prefill | null>(null);
  const [selected, setSelectedRecords] = useState({});

  const [createRecordsDrawer, toggleCreateRecordsDrawer] = useToggle();
  const [updateRecordsDrawer, toggleUpdateRecordsDrawer] = useToggle();
  const actionsRef = useRef<any>();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  // @ts-ignore
  const { pPermissions } = usePagePermissions(pageID);
  const { records, pagination, recordsLoading, error, errorMsg } =
    useQueriedRecords({
      variableValues: { record },
      moduleAPIName: section?.ModuleAPIName,
      query: query,
      queryKeyID: section?.id,
    });

  const updateSortBy = (sortBy: any) => {
    let newQuery = { ...query };

    newQuery.OrderBy = sortBy ? sortBy : null;

    if (newQuery.OrderBy === null) {
      delete newQuery.OrderBy;
    }

    setQuery(newQuery);
  };

  const handleSaveFilters = (filters: any) => {
    const query = { ...section.Query };

    query.Filters = filters?.length ? filters : [];

    updateWidget({ ...section, Query: query });
  };

  const addFieldToQuery = (newField: Field) => {
    if (section?.Query?.Fields?.indexOf(newField?.APIName) >= 0) {
      return;
    }

    const query = {
      ...section.Query,
      Fields: [...section.Query.Fields, newField?.APIName],
    };

    updateWidget({ ...section, Query: query });
  };

  const removeFieldFromQuery = (deleteField: Field) => {
    if (section?.Query?.Fields?.indexOf(deleteField?.APIName) < 0) {
      return;
    }

    const query = {
      ...section.Query,
      Fields: section.Query.Fields.filter(
        (f: string) => f !== deleteField?.APIName
      ),
    };

    updateWidget({ ...section, Query: query });
  };

  const openReorderSettings = () => {
    section._OpenFields = true;
    setSettings(section);
  };

  useEffect(() => {
    let dedup = StrUtils.unique(section?.Query?.Fields);

    let deepClone = structuredClone(section?.Query);

    deepClone.Fields = dedup;

    setQuery(deepClone);
  }, [section?.Query]);

  if (!section?.ModuleAPIName) {
    return <WidgetError onOpenSettings={() => setSettings(section)} />;
  }

  const isSharedView = window.location.pathname.startsWith("/shared-view/");

  const selectedRecords: any[] = [];

  if (!isSharedView && Object.keys(selected).length) {
    for (const key of Object.keys(selected)) {
      selectedRecords.push(records[key]);
    }
  }

  return (
    <>
      {useToolbar && (
        <div className="d-flex p-2 border-bottom justify-content-between align-items-center">
          {section?.ShowToolbar && (
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ gap: "10px" }}
            >
              {/* @ts-ignore */}
              <QuickCreateMenu
                variableIdentifiers={["user"]}
                moduleAPIName={section?.ModuleAPIName}
                prefills={section?.Prefills}
                setSelectedPrefill={setSelectedPrefill}
                toggleCreateRecords={toggleCreateRecordsDrawer}
                separateMenuTitle="Quick Create Settings"
                separateMenuOnClick={() => {
                  section._OpenQuickCreate = true;
                  setSettings(section);
                }}
              />
              <FiltersButton
                currentModule={module}
                savedQuery={section?.Query}
                query={query}
                setQuery={setQuery}
                onSave={handleSaveFilters}
              />
              <SortBy
                savedSortBy={section?.Query?._SelectedOrderBy}
                moduleAPIName={section?.ModuleAPIName}
                updateSortBy={updateSortBy}
                sortBy={
                  query?.OrderBy
                    ? query?.OrderBy?.Field === "id"
                      ? null
                      : query?.OrderBy
                    : null
                }
                onSave={
                  cPermissions.cloud_modify_page ||
                  wPermissions.workspace_modify_page ||
                  pPermissions.page_modify_page
                    ? () =>
                        updateWidget({
                          ...section,
                          Query: { ...query, _SelectedOrderBy: query.OrderBy },
                        })
                    : undefined
                }
              />
              {selectedRecords.length > 0 && (
                <Whisper
                  placement="autoVerticalEnd"
                  trigger="click"
                  ref={actionsRef || null}
                  speaker={
                    <Popover
                      className="sidebar-popover"
                      full
                      onClick={() => actionsRef.current.close()}
                    >
                      {/* @ts-ignore */}
                      <TableActionsMenu
                        moduleAPIName={section?.ModuleAPIName}
                        isSelection={selectedRecords.length > 0}
                        selectedRecords={selected}
                        setSelectedRecords={setSelectedRecords}
                        toggleUpdate={toggleUpdateRecordsDrawer}
                      />
                    </Popover>
                  }
                >
                  <Button size="sm" color="primary" className="mr-1 p-0 px-2">
                    <div className="d-flex align-items-center">
                      {selectedRecords?.length}&nbsp;Selected
                      <i
                        className="mdi mdi-chevron-down ml-2 mr--1"
                        style={{
                          fontSize: 22,
                        }}
                      ></i>
                    </div>
                  </Button>
                </Whisper>
              )}
            </div>
          )}
          {section?.ShowToolbar && (
            <DotsOptionsMenu
              id={section?.id}
              // @ts-ignore
              options={TableSettingsOptions}
              optionsProps={[
                openReorderSettings,
                () => setSettings(section),
                (query?.Fields.includes("id") &&
                  query?.Fields.includes("_RecordName") &&
                  query?.Fields?.length < 4) ||
                  isSharedView,
              ]}
              dotsSize={20}
              placement="autoVerticalEnd"
            />
          )}
        </div>
      )}
      <div>
        {/* @ts-ignore */}
        <AppTable
          moduleAPIName={section?.ModuleAPIName}
          query={query}
          setQuery={setQuery}
          onSort={(sortBy: string) => {
            updateSortBy(sortBy);
          }}
          selectableRows={useToolbar}
          selectedRecords={selected}
          setSelectedRecords={setSelectedRecords}
          columnAdd={useToolbar}
          onColumnAdd={(newField: Field) => addFieldToQuery(newField)}
          onColumnRemove={(deleteField: Field) =>
            removeFieldFromQuery(deleteField)
          }
          data={records}
          pagination={pagination}
          loading={recordsLoading}
          error={error}
          errorMsg={errorMsg}
          widget
          isDrawer={false}
        />
      </div>
      {/* @ts-ignore */}
      <CreateRecordDrawer
        open={createRecordsDrawer}
        onClose={() => {
          toggleCreateRecordsDrawer();
          setSelectedPrefill(null);
        }}
        onCreate={() => {
          toggleCreateRecordsDrawer();
          setSelectedPrefill(null);
        }}
        moduleAPIName={section?.ModuleAPIName}
        prefill={selectedPrefill?.PrefillPairs ?? emptyPrefill}
      />
      {/* @ts-ignore */}
      <UpdateRecordsDrawer
        open={updateRecordsDrawer}
        onClose={toggleUpdateRecordsDrawer}
        records={selected}
        moduleAPIName={section?.ModuleAPIName}
      />
    </>
  );
};

export default TableWidgetV2;
